import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { ProfileData, Category } from '@/src/types/profile';
import { phoneForDb } from '@/src/helpers/phoneForDb';

export const useProfileStore = defineStore('profile', () => {
  // State
  const profile = ref<ProfileData | null>(null);
  const originalProfile = ref<ProfileData | null>(null);
  const pendingUpdates = ref();
  const loading = ref(true);
  const error = ref<string | null>(null);
  const categories = ref<Category[]>([]);
  const changes = ref({});
  const preQuestionsPopupVisible = ref<boolean>(false)

  // Getters
  const hasChanges = computed(() => {
    changes.value = {};

    const normalize = (val: any) =>
      typeof val === 'string' ? val.trim() : typeof val === 'number' ? Number(val) : val;

    for (const field in originalProfile.value) {
      const current = normalize(originalProfile.value[field]);
      const incoming = normalize(profile.value[field]);

      if (field === 'phone') {
        if (phoneForDb(current) !== phoneForDb(incoming)) {
          changes.value[field] = phoneForDb(incoming);
        }
        continue;
      }

      if ((typeof current === 'string' || current === null || typeof current === 'number') && current !== incoming) {
        changes.value[field] = incoming;
      }
    }

    if (
      originalProfile.value?.categories &&
      profile.value?.categories &&
      JSON.stringify(originalProfile.value?.categories.slice().sort()) !==
        JSON.stringify(profile.value?.categories.slice().sort())
    ) {
      changes.value.categories = profile.value.categories;
    }

    return Object.keys(changes.value).length;
  });

  // Actions
  const setProfile = (profileData: ProfileData) => {
    // Create deep copies to ensure they are separate objects
    profile.value = JSON.parse(JSON.stringify(profileData));
    originalProfile.value = JSON.parse(JSON.stringify(profileData));
  };

  const setPendingUpdates = (data: any) => {
    pendingUpdates.value = data;
  };

  const setCategories = (categoriesData: any[]) => {
    categories.value = categoriesData;
  };

  const setLoading = (isLoading: boolean) => {
    loading.value = isLoading;
  };

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage;
  };

  function setPreQuestionsPopupVisible(value: boolean) {
    preQuestionsPopupVisible.value = value;
  }

  const resetProfile = () => {
    if (originalProfile.value) {
      profile.value = JSON.parse(JSON.stringify(originalProfile.value));
    }
  };

  const toggleCategory = (categoryId: number) => {
    if (!profile.value) return;

    const categories = profile.value.categories || [];
    const index = categories.indexOf(categoryId);

    if (index === -1) {
      profile.value = {
        ...profile.value,
        categories: [...categories, categoryId],
      };
    } else {
      const newCategories = categories.filter((id) => id !== categoryId);
      const updates = {
        categories: newCategories,
        primary_category: profile.value.primary_category === categoryId ? null : profile.value.primary_category,
      };

      profile.value = {
        ...profile.value,
        ...updates,
      };
    }
  };

  const setPrimaryCategory = (categoryId: number) => {
    if (!profile.value) return;

    // Only allow setting primary category if it's in selected categories
    const categories = profile.value.categories || [];
    if (categories.includes(categoryId)) {
      profile.value = {
        ...profile.value,
        primary_category: categoryId,
      };
    }
  };

  const getCategoryColor = (category: Category): 'default' | 'primary' | 'warning' | 'feature' | 'success' => {
    const colorMap: Record<string, 'default' | 'primary' | 'warning' | 'feature' | 'success'> = {
      'Love & Relationship': 'primary',
      'Psychic Readings': 'feature',
      'Tarot Readers': 'warning',
      'Spiritual Readings': 'feature',
      'Christian Readings': 'primary',
      'Life Questions': 'success',
      'Astrology Readings': 'warning',
      'Dream Interpretation': 'primary',
    };
    return colorMap[category.name] || 'default';
  };

  return {
    // State
    profile,
    originalProfile,
    pendingUpdates,
    loading,
    error,
    categories,
    changes,
    preQuestionsPopupVisible,

    // Getters
    hasChanges,

    // Actions
    setProfile,
    setCategories,
    setLoading,
    setError,
    resetProfile,
    toggleCategory,
    setPrimaryCategory,
    getCategoryColor,
    setPendingUpdates,
    setPreQuestionsPopupVisible,
  };
});
